import React, { useState, createContext, useCallback } from 'react';

/**
 * Contexto para gestionar los productos agregados a la caja con animaciones.
 * Proporciona `boxItems`, `boxCount`, `addToBox`, `removeFromBox`, `clearBox`, `updateQuantity` y `animatingItems`.
 */
const BoxContext = createContext({
  boxItems: [],
  boxCount: 0,
  addToBox: () => { },
  removeFromBox: () => { },
  clearBox: () => { },
  updateQuantity: () => { },
  getTotal: () => 0,
  animatingItems: [],
  isBoxOpen: false,
  toggleBox: () => { }
});

export const BoxProvider = ({ children }) => {
  const [boxItems, setBoxItems] = useState([]);
  const [animatingItems, setAnimatingItems] = useState([]);
  const [isBoxOpen, setIsBoxOpen] = useState(false);

  const addToBox = useCallback((combo) => {
    setBoxItems((items) => {
      // Verificar si el combo ya existe en el carrito
      const existingItemIndex = items.findIndex(item => item.id === combo.id);

      if (existingItemIndex > -1) {
        // Si existe, incrementar cantidad
        const newItems = [...items];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + 1
        };
        return newItems;
      } else {
        // Si no existe, agregar nuevo item con cantidad 1
        const itemId = Date.now();
        const newItem = {
          ...combo,
          itemId,
          quantity: 1,
          addedAt: new Date().toISOString()
        };

        // Agregar a la lista de animaciones
        setAnimatingItems((items) => [...items, itemId]);

        // Remover de animaciones después de 1.5 segundos
        setTimeout(() => {
          setAnimatingItems((items) => items.filter(id => id !== itemId));
        }, 1500);

        return [...items, newItem];
      }
    });
  }, []);

  const updateQuantity = useCallback((itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromBox(itemId);
      return;
    }

    setBoxItems((items) =>
      items.map(item =>
        item.itemId === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  }, []);

  const removeFromBox = useCallback((itemId) => {
    setBoxItems((items) => items.filter(item => item.itemId !== itemId));
  }, []);

  const clearBox = useCallback(() => {
    setBoxItems([]);
  }, []);

  const toggleBox = useCallback(() => {
    setIsBoxOpen((prev) => !prev);
  }, []);

  const getTotal = useCallback(() => {
    return boxItems.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  }, [boxItems]);

  const boxCount = boxItems.reduce((total, item) => total + item.quantity, 0);

  return (
    <BoxContext.Provider
      value={{
        boxItems,
        boxCount,
        addToBox,
        removeFromBox,
        clearBox,
        updateQuantity,
        getTotal,
        animatingItems,
        isBoxOpen,
        toggleBox
      }}
    >
      {children}
    </BoxContext.Provider>
  );
};

export default BoxContext;